// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React from "react";
import { createPortal } from "react-dom";
import type { Theme } from "../lib/theme";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

/**
 * What the figures you have selected come to.
 *
 * The bottom-right readout a spreadsheet gives you, in the same corner and
 * saying the same things, because that is where an estimator's eye already
 * goes. It answers a question asked for a second and then dropped -- what do
 * these five alternates come to? -- without making the document carry a
 * calculation nobody asked it to keep.
 *
 * Lives here because the estimate grid and the Decisions report both show it,
 * and a second copy would have drifted in a week.
 *
 * It never takes the pointer: it hovers over the rows being selected, and a
 * drag that ended on top of it would otherwise stop short.
 *
 * Portalled to the body, for the reason the status menu is: position:fixed
 * is measured against the nearest transformed ancestor rather than the
 * viewport, so a "fixed" overlay rendered inside a tab is not fixed to
 * anything you can see. Measured here at 1704px down the page on Decisions
 * while sitting correctly on the estimate grid, which only differed in
 * rendering it higher up the tree. The portal makes the position independent
 * of whatever any ancestor does later; it also means declaring the font,
 * since nothing under the body inherits the app's.
 */
export default function SelectionStatus({
  T, count, value, valueLabel = "Total", average,
}: {
  T: Theme;
  count: number;
  /** Already formatted -- the caller knows whether these are dollars. */
  value: string;
  valueLabel?: string;
  /** Omitted for a single figure, whose average is itself. */
  average?: string | null;
}) {
  if (!count) return null;
  const sep = <span style={{ color: T.borderSecondary }}>|</span>;
  return createPortal((
    <div style={{ position: "fixed", bottom: 14, right: 20, background: T.bgInput,
      border: `1px solid ${T.borderSecondary}`, borderRadius: 3,
      padding: "5px 16px", display: "flex", gap: 20, fontSize: 11, color: T.textTertiary,
      zIndex: 150, boxShadow: "0 4px 20px #00000070", pointerEvents: "none", fontFamily: FONT }}>
      <span>Count: <span style={{ color: T.textPrimary, fontWeight: 600 }}>{count}</span></span>
      {average != null && <>
        {sep}
        <span>Average: <span style={{ color: T.textPrimary, fontWeight: 600 }}>{average}</span></span>
      </>}
      {sep}
      <span>{valueLabel}: <span style={{ color: T.borderAccent, fontWeight: 600 }}>{value}</span></span>
    </div>
  ), document.body);
}
